import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';

import { supabase } from '@/lib/supabase';

type OAuthProvider = 'google' | 'discord';

interface AuthContextValue {
	user: User | null;
	session: Session | null;
	loading: boolean;
	signInWithEmail: (email: string, password: string) => Promise<{ error: string | null }>;
	signUpWithEmail: (email: string, password: string) => Promise<{ error: string | null }>;
	signInWithOAuth: (
		provider: OAuthProvider,
		redirectTo?: string,
	) => Promise<{ error: string | null }>;
	signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [session, setSession] = useState<Session | null>(null);
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		supabase.auth.getSession().then(({ data }) => {
			setSession(data.session);
			setUser(data.session?.user ?? null);
			setLoading(false);
		});

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, nextSession) => {
			setSession(nextSession);
			setUser(nextSession?.user ?? null);
		});

		return () => subscription.unsubscribe();
	}, []);

	const signInWithEmail: AuthContextValue['signInWithEmail'] = async (email, password) => {
		const { error } = await supabase.auth.signInWithPassword({ email, password });
		return { error: error?.message ?? null };
	};

	const signUpWithEmail: AuthContextValue['signUpWithEmail'] = async (email, password) => {
		const { error } = await supabase.auth.signUp({ email, password });
		return { error: error?.message ?? null };
	};

	const signInWithOAuth: AuthContextValue['signInWithOAuth'] = async (provider, redirectTo) => {
		const { error } = await supabase.auth.signInWithOAuth({
			provider,
			options: {
				redirectTo: redirectTo ?? `${window.location.origin}${window.location.pathname}`,
			},
		});
		return { error: error?.message ?? null };
	};

	const signOut = async () => {
		await supabase.auth.signOut();
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				session,
				loading,
				signInWithEmail,
				signUpWithEmail,
				signInWithOAuth,
				signOut,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
	return ctx;
}
